import { serviceCountryName } from './serviceRequestCountries.js';

export const S16_SCENARIOS = ['Fire / Explosion', 'Active Threat', 'Severe Weather', 'Chemical Spill / HAZMAT', 'Medical Emergency', 'Other'] as const;
export const S16_REVIEW_FREQUENCIES = ['Quarterly', 'Every six months', 'Annually'] as const;
export const S16_EXERCISE_TYPES = ['Tabletop', 'Evacuation drill', 'Functional exercise'] as const;
export type S16Checklist = { role: string; tasks: string[] };
export type S16Intake = {
    organizationName: string;
    industry: string;
    sites: string[];
    scenario: string;
    siteDetails: string;
    evacuationRoutes: string[];
    rallyPoints: string[];
    equipmentRequirements: string[];
    tacticalChecklists: S16Checklist[];
    responseTeam: string;
    activationTriggers: string;
    emergencyContacts: string;
    communicationsPlan: string;
    assistancePlan: string;
    reviewFrequency: string;
    exerciseType: string;
    templateContext?: string;
};

const text = (value: unknown, maximum = 4000) => String(value ?? '').trim().slice(0, maximum);
const strings = (value: unknown, maximum = 40) => Array.isArray(value)
    ? [...new Set(value.map(item => text(item, 1000)).filter(Boolean))].slice(0, maximum) : [];

export function emptyS16Intake(): S16Intake {
    return { organizationName: '', industry: '', sites: [], scenario: '', siteDetails: '', evacuationRoutes: [],
        rallyPoints: [], equipmentRequirements: [], tacticalChecklists: [], responseTeam: '', activationTriggers: '',
        emergencyContacts: '', communicationsPlan: '', assistancePlan: '', reviewFrequency: '', exerciseType: '' };
}

export function prefillS16Intake(organization: any): S16Intake {
    const address = (value: any) => [value?.addressLine1, value?.addressLine2, value?.city,
        value?.administrativeArea, serviceCountryName(value?.country || value?.countryCode)]
        .map(part => text(part, 160)).filter(Boolean).join(', ');
    return { ...emptyS16Intake(), organizationName: text(organization?.name || organization?.brandName, 240),
        industry: text(organization?.industry, 160), sites: strings([address(organization?.headquartersAddress),
            ...(Array.isArray(organization?.additionalLocations) ? organization.additionalLocations.map(address) : [])], 20) };
}

export function cleanS16Intake(source: any): S16Intake {
    const input = source && typeof source === 'object' ? source : {};
    return { organizationName: text(input.organizationName, 240), industry: text(input.industry, 160),
        sites: strings(input.sites, 20), scenario: text(input.scenario, 240), siteDetails: text(input.siteDetails),
        evacuationRoutes: strings(input.evacuationRoutes), rallyPoints: strings(input.rallyPoints),
        equipmentRequirements: strings(input.equipmentRequirements),
        tacticalChecklists: (Array.isArray(input.tacticalChecklists) ? input.tacticalChecklists : []).slice(0, 20)
            .map((item: any) => ({ role: text(item?.role, 160), tasks: strings(item?.tasks) })),
        responseTeam: text(input.responseTeam), activationTriggers: text(input.activationTriggers),
        emergencyContacts: text(input.emergencyContacts), communicationsPlan: text(input.communicationsPlan),
        assistancePlan: text(input.assistancePlan),
        reviewFrequency: S16_REVIEW_FREQUENCIES.includes(input.reviewFrequency) ? input.reviewFrequency : '',
        exerciseType: S16_EXERCISE_TYPES.includes(input.exerciseType) ? input.exerciseType : '',
        ...(text(input.templateContext, 20000) ? { templateContext: text(input.templateContext, 20000) } : {}) };
}

export function missingS16Intake(source: any): string[] {
    const intake = cleanS16Intake(source);
    const missing: string[] = [];
    if (!intake.organizationName || !intake.industry || !intake.sites.length)
        missing.push('Organisation, industry, and sites');
    if (!intake.scenario)
        missing.push('Emergency scenario');
    if (!intake.evacuationRoutes.length || !intake.rallyPoints.length)
        missing.push('Evacuation routes and rally points');
    if (!intake.equipmentRequirements.length)
        missing.push('Equipment requirements');
    if (!intake.tacticalChecklists.length || intake.tacticalChecklists.some(item => !item.role || !item.tasks.length)
        || new Set(intake.tacticalChecklists.map(item => item.role.toLowerCase())).size !== intake.tacticalChecklists.length)
        missing.push('Response roles and duty checklists');
    for (const [key, label] of [['responseTeam', 'Named response team'], ['activationTriggers', 'Activation triggers'],
        ['emergencyContacts', 'Emergency contacts'], ['communicationsPlan', 'Communications plan'],
        ['assistancePlan', 'Assistance and medical response plan']] as const)
        if (!intake[key])
            missing.push(label);
    if (!intake.reviewFrequency || !intake.exerciseType)
        missing.push('Review and exercise schedule');
    return missing;
}
